import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
    { id: "servizi", label: "Servizi" },
    { id: "chi-siamo", label: "Chi siamo" },
    { id: "contatti", label: "Contatti" },
];

function scrollToId(id) {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) {
        window.__lenis.scrollTo(el, { offset: -80, duration: 1.1 });
    } else {
        el.scrollIntoView({ behavior: "smooth" });
    }
}

export default function Header() {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
            window.__lenis?.stop();
        } else {
            document.body.style.overflow = "";
            window.__lenis?.start();
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const go = (e, id) => {
        e.preventDefault();
        if (open) {
            setOpen(false);
            setTimeout(() => {
                if (pathname !== "/") {
                    navigate("/");
                    setTimeout(() => scrollToId(id), 150);
                } else {
                    scrollToId(id);
                }
            }, 250);
        } else if (pathname !== "/") {
            navigate("/");
            setTimeout(() => scrollToId(id), 150);
        } else {
            scrollToId(id);
        }
    };

    const goHome = (e) => {
        e.preventDefault();
        setOpen(false);
        if (pathname !== "/") {
            navigate("/");
        } else if (window.__lenis) {
            window.__lenis.scrollTo(0, { duration: 1 });
        } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    return (
        <header className="sticky top-0 z-40 border-b border-hairline bg-paper">
            <div className="flex h-16 items-center justify-between px-6 md:h-20 md:px-12">
                <a
                    href="/"
                    onClick={goHome}
                    data-testid="header-logo"
                    className="flex items-center gap-2.5"
                    aria-label="Ecohouse S.r.l.s. — Home"
                >
                    <svg width="22" height="22" viewBox="0 0 64 64" aria-hidden="true">
                        <rect width="64" height="64" fill="#062D1C" />
                        <path d="M12 34 L32 16 L52 34" fill="none" stroke="#F4F5F0" strokeWidth="4.5" strokeLinecap="square" />
                        <path d="M20 34 V46 H30" fill="none" stroke="#F4F5F0" strokeWidth="4.5" strokeLinecap="square" />
                        <circle cx="43" cy="43" r="5.5" fill="#F4F5F0" />
                    </svg>
                    <span className="stretch-wide text-lg font-black tracking-[-0.02em] text-leaf md:text-xl">ECOHOUSE</span>
                </a>

                <nav className="hidden items-center gap-10 md:flex">
                    {LINKS.map((l) => (
                        <a
                            key={l.id}
                            href={`/#${l.id}`}
                            onClick={(e) => go(e, l.id)}
                            data-testid={`header-nav-link-${l.id}`}
                            className="font-mono text-xs uppercase tracking-[0.2em] text-leafdeep/70 transition-colors hover:text-leafdeep"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <button
                    type="button"
                    className="md:hidden"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="header-mobile-menu-button"
                    aria-label={open ? "Chiudi menu" : "Apri menu"}
                    aria-expanded={open}
                >
                    {open ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 top-16 z-30 flex flex-col bg-ink px-8 pb-10 pt-6 text-paper md:hidden"
                        data-testid="header-mobile-overlay"
                    >
                        {LINKS.map((l, i) => (
                            <a
                                key={l.id}
                                href={`/#${l.id}`}
                                onClick={(e) => go(e, l.id)}
                                data-testid={`header-mobile-link-${l.id}`}
                                className="flex items-baseline justify-between border-b border-paper/15 py-6"
                            >
                                <span className="stretch-wide text-4xl font-black tracking-tight">{l.label}</span>
                                <span className="font-mono text-xs text-paper/50">0{i + 1}</span>
                            </a>
                        ))}
                        <Link
                            to="/privacy"
                            onClick={() => setOpen(false)}
                            className="mt-auto font-mono text-xs uppercase tracking-[0.2em] text-paper/60"
                            data-testid="header-mobile-link-privacy"
                        >
                            Privacy policy
                        </Link>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
